import {useEffect, useState} from 'react';

// One ResizeObserver serves every cell using this hook, routing each entry to its element's callback, so a
// long list does not create an observer per cell. It is created on the first observe and disconnected when
// the last element leaves, so nothing outlives the cells that use it.
//
// Cells are measured on the next animation frame, not inside the observer's callback. A cell that turns
// truncated re-renders (it gains a tooltip), and a layout change made inside the callback would be reported
// again in the same frame, which the browser flags as "ResizeObserver loop completed with undelivered
// notifications". A cell that leaves before the frame is skipped, since its callback is gone by then.
const callbacks = new Map<Element, () => void>();
let sharedObserver: ResizeObserver | null = null;

function observe(element: Element, callback: () => void): void {
	if (sharedObserver === null) {
		sharedObserver = new ResizeObserver(entries => {
			const targets = entries.map(entry => entry.target);
			window.requestAnimationFrame(() => targets.forEach(target => callbacks.get(target)?.()));
		});
	}
	callbacks.set(element, callback);
	sharedObserver.observe(element);
}

function unobserve(element: Element): void {
	callbacks.delete(element);
	if (sharedObserver === null) return;
	sharedObserver.unobserve(element);
	if (callbacks.size === 0) {
		sharedObserver.disconnect();
		sharedObserver = null;
	}
}

// Measures whenever the element resizes - dragging a column, hiding or showing one, grouping, and window
// resizes that change the cell's width - through the shared ResizeObserver, which also reports each element
// once when observation starts. Layout is never read during render. Where ResizeObserver does not exist
// (jsdom), it measures once after mount. Returns a callback ref and the current state.
function useIsTruncated<T extends HTMLElement>(isCutOff: (element: T) => boolean): [(node: T | null) => void, boolean] {
	const [element, setElement] = useState<T | null>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		if (element === null) return;
		const measure = () => setIsTruncated(isCutOff(element));
		if (typeof ResizeObserver === 'undefined') {
			measure();
			return;
		}
		observe(element, measure);
		return () => unobserve(element);
		// isCutOff is one of the two module-level tests below, so it never changes between renders.
	}, [element]);

	return [setElement, isTruncated];
}

const isWiderThanBox = (element: HTMLElement): boolean => element.scrollWidth > element.clientWidth;
// A clamped box that shows every line still reports a scrollHeight a couple of pixels over its clientHeight
// (glyph overhang and line-height rounding: a 16px one-line label measures 18px). A cut-off name hides a
// whole line, so only overflow of more than half a line counts.
const isTallerThanBox = (element: HTMLElement): boolean => {
	const style = window.getComputedStyle(element);
	const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2 || 0;
	return element.scrollHeight - element.clientHeight > lineHeight / 2;
};

/** Detects whether a one-line (`white-space: nowrap; text-overflow: ellipsis`) element is cut off, by
 * comparing its full (scroll) width against its visible (client) width. A height check cannot see this:
 * nowrap text never wraps, so the box never grows taller than one line. */
export function useIsTruncatedWidth<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, boolean] {
	return useIsTruncated<T>(isWiderThanBox);
}

/** Detects whether a line-clamped (`-webkit-line-clamp`) element is cut off, by comparing its full (scroll)
 * height against its visible (client) height. Clamped text wraps before it clips, so it only ever
 * overflows downwards. */
export function useIsTruncatedHeight<T extends HTMLElement = HTMLElement>(): [(node: T | null) => void, boolean] {
	return useIsTruncated<T>(isTallerThanBox);
}
